import {
  navigation_collapsed_styles_default
} from "./chunk.EDKP2JFD.js";
import {
  navigation_expanded_styles_default
} from "./chunk.OZ23FGCQ.js";
import {
  lockBodyScrolling,
  unlockBodyScrolling
} from "./chunk.XS4D3FDG.js";
import {
  breakpointPixels
} from "./chunk.JI3RRLCG.js";
import {
  animateTo,
  stopAnimations
} from "./chunk.JEYCYKK2.js";
import {
  emit
} from "./chunk.C4ZTYQN4.js";
import {
  HasSlotController
} from "./chunk.CLKP6Z3C.js";
import {
  getAnimation,
  setDefaultAnimation
} from "./chunk.456HIP2G.js";
import {
  o
} from "./chunk.OBPYZVEK.js";
import {
  e,
  i,
  n,
  t
} from "./chunk.D5EJUUKV.js";
import {
  $,
  s,
  w
} from "./chunk.4JTBA2KG.js";
import {
  __decorateClass
} from "./chunk.K2NRSETB.js";

// src/components/navigation/navigation.ts
import { t as t2 } from "i18next";
var FadvNavigation = class extends s {
  constructor() {
    super(...arguments);
    this.hasSlot = new HasSlotController(this, "[default]", "start", "end");
    this.breakpoint = "sm";
    this.label = t2("components:navigation.main");
    this.checkOnClick = false;
    this.open = false;
    this.mode = "default";
    this.isCollapsed = document.body.clientWidth < breakpointPixels[this.breakpoint];
    this.hasScrollControls = false;
  }
  get menuOrientation() {
    if (this.mode === "collapsed")
      return "vertical";
    if (this.mode === "expanded")
      return "horizontal";
    return this.isCollapsed ? "vertical" : "horizontal";
  }
  async connectedCallback() {
    super.connectedCallback();
    await this.updateComplete;
    this.handleResize = this.handleResize.bind(this);
    this.handleWindowResize = this.handleWindowResize.bind(this);
    this.resizeObserver = new ResizeObserver(this.handleResize);
    this.resizeObserver.observe(document.body);
    window.addEventListener("resize", this.handleWindowResize);
    requestAnimationFrame(() => {
      this.handleWindowResize();
    });
  }
  disconnectedCallback() {
    this.resizeObserver.disconnect();
    window.removeEventListener("resize", this.handleWindowResize);
    unlockBodyScrolling(this);
    super.disconnectedCallback();
  }
  handleResize() {
    switch (this.mode) {
      case "collapsed":
        this.hasScrollControls = false;
        break;
      case "expanded":
        this.hasScrollControls = this.navMenu.scrollWidth > this.navMenu.clientWidth;
        break;
      default:
        this.isCollapsed = document.body.clientWidth < breakpointPixels[this.breakpoint];
        if (!this.isCollapsed) {
          this.hasScrollControls = this.navMenu.scrollWidth > this.navMenu.clientWidth;
        }
        break;
    }
  }
  handleWindowResize() {
    if (this.isCollapsed) {
      this.hasScrollControls = this.navMenu.scrollHeight > this.navMenu.clientHeight;
    }
  }
  async toggleOpenState() {
    stopAnimations(this.nav);
    stopAnimations(this.overlay);
    if (!this.open) {
      lockBodyScrolling(this);
      this.nav.hidden = false;
      this.overlay.hidden = false;
      const navAnimation = getAnimation(this, "navigation.open");
      const overlayAnimation = getAnimation(this, "navigation-overlay.open");
      animateTo(this.overlay, overlayAnimation.keyframes, overlayAnimation.options);
      await animateTo(this.nav, navAnimation.keyframes, navAnimation.options);
      this.open = true;
      this.nav.focus();
      this.liveRegion.textContent = t2("components:navigation.opened");
      emit(this, "sl-after-show");
    } else {
      const navAnimation = getAnimation(this, "navigation.close");
      const overlayAnimation = getAnimation(this, "navigation-overlay.close");
      animateTo(this.overlay, overlayAnimation.keyframes, overlayAnimation.options);
      await animateTo(this.nav, navAnimation.keyframes, navAnimation.options);
      this.open = false;
      this.nav.hidden = true;
      this.overlay.hidden = true;
      this.openButton.focus();
      this.liveRegion.textContent = t2("components:navigation.closed");
      unlockBodyScrolling(this);
      emit(this, "sl-after-hide");
    }
  }
  handleSelect(event) {
    if (this.isCollapsed) {
      this.toggleOpenState();
    }
    if (!this.checkOnClick)
      return;
    const menuItem = event.detail.item;
    const menuItems = this.querySelectorAll("fadv-menu-item");
    menuItems.forEach((x) => x.checked = false);
    menuItem.checked = true;
  }
  handleScrollToStart() {
    if (!this.isCollapsed) {
      this.navMenu.scroll({
        left: this.navMenu.scrollLeft - this.navMenu.clientWidth,
        behavior: "smooth"
      });
    }
  }
  handleScrollToEnd() {
    if (!this.isCollapsed) {
      this.navMenu.scroll({
        left: this.navMenu.scrollLeft + this.navMenu.clientWidth,
        behavior: "smooth"
      });
    }
  }
  handleKeyDown(event) {
    if (this.open && event.key === "Escape") {
      this.toggleOpenState();
    }
  }
  renderScrollStart() {
    if (!this.hasScrollControls)
      return w;
    return $`<sl-icon-button class="navigation__scroll-button navigation__scroll-button--start" name="chevron-left" library="solid" label=${t2("components:common.scrollToStart")} @click=${this.handleScrollToStart} ></sl-icon-button>`;
  }
  renderScrollEnd() {
    if (!this.hasScrollControls)
      return w;
    return $`<sl-icon-button class="navigation__scroll-button navigation__scroll-button--end" name="chevron-right" library="solid" label=${t2("components:common.scrollToEnd")} @click=${this.handleScrollToEnd} ></sl-icon-button>`;
  }
  renderInternal() {
    return $`<div class="navigation__start"><slot name="start"></slot></div>${this.renderScrollStart()}<div class="navigation__main"><fadv-menu orientation=${this.menuOrientation} @sl-select=${this.handleSelect}><slot></slot></fadv-menu></div>${this.renderScrollEnd()}<div class="navigation__end"><slot name="end"></slot></div>`;
  }
  renderCollapsedNavButton(className) {
    return $`<button class=${className} aria-expanded=${this.open ? "true" : "false"} aria-controls="navigation" @click=${this.toggleOpenState}><fadv-visually-hidden>${t2("components:navigation.collapsedButton")}</fadv-visually-hidden><div aria-hidden="true" class="navigation__collapsed-nav-icon"><span></span><span></span><span></span><span></span></div></button>`;
  }
  renderCollapsed() {
    return $`<div class=${o({
      navigation: true,
      "navigation-collapsed": true,
      "navigation-collapsed--open": this.open,
      "navigation--has-start": this.hasSlot.test("start"),
      "navigation--has-end": this.hasSlot.test("end")
    })} @keydown=${this.handleKeyDown} ><div class="navigation__overlay" aria-hidden="true" .hidden=${!this.open} @click=${this.toggleOpenState}></div><nav id="navigation" role="navigation" aria-hidden=${this.open ? "false" : "true"} aria-label=${this.label} .hidden=${!this.open} class="navigation__collapsed-container" tabindex=${this.open ? "0" : "-1"} ><div class="navigation__content">${this.renderInternal()}</div><div class="navigation__aside">${this.renderCollapsedNavButton("navigation__trigger navigation__trigger-close")}</div></nav>${this.renderCollapsedNavButton("navigation__trigger navigation__trigger-open")}<fadv-visually-hidden class="navigation__live-region" aria-live="polite"></fadv-visually-hidden></div>`;
  }
  renderExpanded() {
    return $`<nav class=${o({
      navigation: true,
      "navigation-expanded": true,
      "navigation--has-start": this.hasSlot.test("start"),
      "navigation--has-end": this.hasSlot.test("end"),
      "navigation--has-scroll-controls": this.hasScrollControls
    })} role="navigation" aria-label=${this.label} >${this.renderInternal()}</nav>`;
  }
  render() {
    if (this.mode === "default" && this.isCollapsed || this.mode === "collapsed") {
      return this.renderCollapsed();
    }
    return this.renderExpanded();
  }
};
FadvNavigation.styles = [navigation_expanded_styles_default, navigation_collapsed_styles_default];
__decorateClass([
  e()
], FadvNavigation.prototype, "breakpoint", 2);
__decorateClass([
  e()
], FadvNavigation.prototype, "label", 2);
__decorateClass([
  e({ type: Boolean, attribute: "check-on-click" })
], FadvNavigation.prototype, "checkOnClick", 2);
__decorateClass([
  e({ reflect: true, type: Boolean })
], FadvNavigation.prototype, "open", 2);
__decorateClass([
  e()
], FadvNavigation.prototype, "mode", 2);
__decorateClass([
  t()
], FadvNavigation.prototype, "isCollapsed", 2);
__decorateClass([
  t()
], FadvNavigation.prototype, "hasScrollControls", 2);
__decorateClass([
  i("nav")
], FadvNavigation.prototype, "nav", 2);
__decorateClass([
  i(".navigation-expanded")
], FadvNavigation.prototype, "desktop", 2);
__decorateClass([
  i(".navigation-collapsed")
], FadvNavigation.prototype, "collapsed", 2);
__decorateClass([
  i(".navigation__main")
], FadvNavigation.prototype, "navMenu", 2);
__decorateClass([
  i(".navigation__trigger-open")
], FadvNavigation.prototype, "openButton", 2);
__decorateClass([
  i(".navigation__trigger-close")
], FadvNavigation.prototype, "closeButton", 2);
__decorateClass([
  i(".navigation__live-region")
], FadvNavigation.prototype, "liveRegion", 2);
__decorateClass([
  i(".navigation__overlay")
], FadvNavigation.prototype, "overlay", 2);
FadvNavigation = __decorateClass([
  n("fadv-navigation")
], FadvNavigation);
var duration = 300;
setDefaultAnimation("navigation.open", {
  keyframes: [{ left: "-100%" }, { left: "0" }],
  options: { duration, easing: "ease-in-out" }
});
setDefaultAnimation("navigation-overlay.open", {
  keyframes: [{ backgroundColor: "transparent" }, { backgroundColor: "var(--nav-collapsed-overlay)" }],
  options: { duration, easing: "ease-in-out" }
});
setDefaultAnimation("navigation.close", {
  keyframes: [{ left: "0" }, { left: "-100%" }],
  options: { duration, easing: "ease-in-out" }
});
setDefaultAnimation("navigation-overlay.close", {
  keyframes: [{ backgroundColor: "var(--nav-collapsed-overlay)" }, { backgroundColor: "transparent" }],
  options: { duration, easing: "ease-in-out" }
});

export {
  FadvNavigation
};
